import React, {useEffect} from 'react';
import {Flex, IconButton, Menu, MenuButton, MenuItem, MenuList, Td, Text} from "@chakra-ui/react";
import {BsThreeDots} from "react-icons/bs";
import {MdFeedback} from "react-icons/md";
import {CiCircleRemove} from "react-icons/ci";
import {PiPlayCircleBold} from "react-icons/pi";
import {IProject} from "../../models/interfaces/project.interface.ts";
import {useProject} from "../../hooks/useProject.ts";
import {IItemProgress} from "../../models/interfaces/itemProgress.interface.ts";
import SmartIcon from "../SmartIcon.tsx";
import {downloadFeatures} from "../../services/projectService.ts";
import AppSpinner from "../AppSpinner.tsx";
import {useOpenSeriesModal} from "../../hooks/useOpenSeriesModal.tsx";
import {LiaEdit} from "react-icons/lia";

interface ProjectTableActionsColumnProps {
  project: IProject;
  openFeedbackDrawer: (projectId: string, targetLanguage: string[]) => void;
  presentPreview: (projectId: string) => void;
  setSelectedProjectId: (id: string | null) => void;
  onAlertDialogOpen: () => void;
}

const ProjectTableActionsColumn: React.FC<ProjectTableActionsColumnProps> = (
  {
    project,
    openFeedbackDrawer,
    presentPreview,
    setSelectedProjectId,
    onAlertDialogOpen,
  }: ProjectTableActionsColumnProps) => {
  const openSeriesModal: (seriesId: string) => void = useOpenSeriesModal()
  const {extractAllFeatureItems, calculateProjectPercentage, extractTargetTranslationsLanguages} = useProject()
  const [isDownloadingAvailable, setIsDownloadingAvailable] = React.useState<boolean>(false);

  const projectPercentage: number | null = calculateProjectPercentage(project);
  const enableDownloadAvailable: boolean = project.transcription?.status == "co" || extractAllFeatureItems(project.featuresStates).some((item: IItemProgress) => item.pct === 100);

  useEffect(() => {
    if (isDownloadingAvailable) {
      const timer = setTimeout(() => setIsDownloadingAvailable(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isDownloadingAvailable]);

  const triggerAction = (
    e: React.MouseEvent | React.KeyboardEvent,
    callback: () => void
  ) => {
    e.stopPropagation();
    e.preventDefault();
    callback();
  };

  const downloadAvailable = () => {
    const info: {
      subtitles?: string[];
      audioDescriptions?: string[];
      dialogueList?: boolean;
      extendedMetadata?: boolean;
    } = {};
    setIsDownloadingAvailable(true);
    const completedSubtitles: string[] = Object.keys(project.featuresStates.subtitleTranslations || {}).filter((lang) => project.featuresStates.subtitleTranslations![lang].pct === 100 ? lang : undefined);
    if (project.transcription?.status == "co") {
      completedSubtitles.unshift(project.sourceLanguage)
    }
    const completedAudioDescriptions: string[] = Object.keys(project.featuresStates.audioDescriptions || {}).filter((lang) => project.featuresStates.audioDescriptions![lang].pct === 100 ? lang : undefined);
    const isDialogueListCompleted: boolean = project.featuresStates.dialogueList?.pct === 100;
    const isExtendedMetadataCompleted: boolean = project.featuresStates.extendedMetadata?.pct === 100;
    if (completedSubtitles.length > 0) {
      info["subtitles"] = completedSubtitles;
    }
    if (completedAudioDescriptions.length > 0) {
      info["audioDescriptions"] = completedAudioDescriptions;
    }
    if (isDialogueListCompleted) {
      info["dialogueList"] = true;
    }
    if (isExtendedMetadataCompleted) {
      info["extendedMetadata"] = true;
    }
    downloadFeatures(project.id, project.name, info).catch(console.error);
  }

  return (
    <Td textAlign="right">
      <Menu>
        <MenuButton
          as={IconButton}
          icon={isDownloadingAvailable ? <AppSpinner size={"md"}/> : <BsThreeDots size={20}/>}
          variant="ghost"
          color="white"
          bg="transparent"
          _hover={{bg: "gray.700"}}
          _active={{bg: "gray.700"}}
          onClick={(e: React.MouseEvent | React.KeyboardEvent) => e.stopPropagation()}
        />
        <MenuList
          bg="gray.800"
          border="1px solid"
          borderColor="gray.700"
          shadow="lg"
          rounded="md"
          minW="150px"
          p={2}
        >
          {enableDownloadAvailable && (
            <MenuItem
              bg="gray.800"
              _hover={{bg: "gray.700", opacity: 0.9}}
              rounded="md"
              py={2}
              px={3}
              onClick={(e: React.MouseEvent | React.KeyboardEvent) => triggerAction(e, downloadAvailable)}
            >
              <Flex flex={1} align="center" gap={2}>
                <SmartIcon name="download" className="w-5 h-5 dark:fill-n-300/50"/>
                <Text fontSize="16px" color="white">Download Available</Text>
              </Flex>
            </MenuItem>
          )}
          {projectPercentage == 100 && (
            <>
              <MenuItem
                bg="gray.800"
                _hover={{bg: "gray.700", opacity: 0.9}}
                rounded="md"
                py={2}
                px={3}
                onClick={(e: React.MouseEvent | React.KeyboardEvent) => triggerAction(e, () => presentPreview(project.id))}
              >
                <Flex flex={1} align="center" gap={2}>
                  <PiPlayCircleBold size={18} color="white"/>
                  <Text fontSize="16px" color="white">Preview</Text>
                </Flex>
              </MenuItem>
              <MenuItem
                bg="gray.800"
                _hover={{bg: "gray.700", opacity: 0.9}}
                rounded="md"
                py={2}
                px={3}
                onClick={(e: React.MouseEvent | React.KeyboardEvent) => triggerAction(e, () => openFeedbackDrawer(project.id, extractTargetTranslationsLanguages(project)))}
              >
                <Flex flex={1} align="center" gap={2}>
                  <MdFeedback size={18} color="white"/>
                  <Text fontSize="16px" color="white">Feedback</Text>
                </Flex>
              </MenuItem>
            </>
          )}
          {/*Series modal*/}
          {project.seriesName && (
            <MenuItem
              bg="gray.800"
              _hover={{bg: "gray.700", opacity: 0.9}}
              rounded="md"
              py={2}
              px={3}
              onClick={(e: React.MouseEvent | React.KeyboardEvent) => triggerAction(e, () => openSeriesModal(project.seriesName))}
            >
              <Flex flex={1} align="center" gap={2}>
                <SmartIcon name={LiaEdit} className="h-5 w-5 hover:fill-white cursor-pointer"/>
                <Text fontSize="16px" color="white">Series Info</Text>
              </Flex>
            </MenuItem>
          )}

          {/*delete*/}
          <MenuItem
            bg="gray.800"
            _hover={{bg: "gray.700", opacity: 0.9}}
            rounded="md"
            py={2}
            px={3}
            onClick={(e: React.MouseEvent | React.KeyboardEvent) => {
              e.stopPropagation();
              setSelectedProjectId(project.id);
              onAlertDialogOpen();
            }}
          >
            <Flex flex={1} align="center" gap={2}>
              <CiCircleRemove size={20} color="red"/>
              <Text fontSize="16px" color="white">Delete</Text>
            </Flex>
          </MenuItem>
        </MenuList>
      </Menu>
    </Td>
  )
}

export default ProjectTableActionsColumn;